import { Box, Button, Grid, IconButton, Tooltip, Typography } from "@mui/material";
import LabelPrinter from "../../../../../../../components/LabelPrinter";
import { formatarNumero, formatarValorMonetario } from "../../../../../../../helpers/masks";
import { useFormContext } from "react-hook-form";
import { useMemo, useState } from "react";
import { useQuery } from "react-query";
import axiosClient from "../../../../../../../lib/axios";
import LoadingApiQuery from "../../../../../../../components/LoadingApiQuery";
import DialogDetalhesTitulos from "./DialogDetalhesTitulos";
import DialogSelecionarData from "./DialogSelecionarData";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import PrintIcon from "@mui/icons-material/Print";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import useImprimirArquivo from "../../../../../../../lib/useImprimirArquivo";
import { SimulacaoTitulo } from "../../../../../../../types/api/financeiro/simular-recebimento-titulos";
import { IDataListaTitulosReceber } from "../../../../../../../types/api/financeiro/pesquisar-titulos-receber";
import moment from "moment";
import { usePermission } from "../../../../../../../components/PermissionGuard";

export default function TitulosSelecionados() {

  //Form context
  const { getValues, watch, setValue } = useFormContext();

  const imprimirArquivo = useImprimirArquivo();
  const formRecb = watch("dados_recebimento.forma_recebimento");
  const dataReceb = watch("finalizacao.data_recebimento");
  const titulos: IDataListaTitulosReceber[] = getValues("selecionar_titulos");

  const habilitarQuery: boolean =
    titulos.length > 0 && getValues("stepper") > 0;
  //

  const dataMaisRecente =
    titulos.length > 0
      ? moment(
        titulos.reduce((maisRecente, titulo) => {
          return moment(titulo.data_emissao).isAfter(
            moment(maisRecente.data_emissao)
          )
            ? titulo
            : maisRecente;
        }).data_emissao
      ).format("YYYY-MM-DD")
      : null;

  const [detalhesModal, setDetalhesModal] = useState({
    openModal: false,
    titulos: [] as IDataListaTitulosReceber[],
  });
  const [modalDataRecb, setModalDataRecb] = useState(false);
  const [mostrarValor, setMostrarValor] = useState(false);

  //Querys e mutations
  const { data, isLoading } = useQuery({
    queryKey: ["simular-recebimento-titulos", titulos, dataReceb],
    queryFn: () =>
      axiosClient.get("financeiro/contas-receber/simular-recebimento-titulos", {
        params: {
          titulos: titulos.map((titulo) => titulo.id),
          data_recebimento: dataReceb.format("YYYY-MM-DD"),
        },
      }),
    enabled: habilitarQuery,
  });
  //

  //Valores importantes
  const podeDetalhar = usePermission('financeiro.contas-receber-detalhar-calculo')
  const dataDetalhes: SimulacaoTitulo = useMemo(() => data?.data, [data]);

  const { totalAtualizado, juros, saldoTotal, quantidadeItens } =
    useMemo(() => {
      const resultado = {
        juros: 0,
        saldoTotal: 0,
        totalAtualizado: 0,
        quantidadeItens: 0,
      };
      if (!dataDetalhes) return resultado;

      resultado.quantidadeItens = dataDetalhes.titulos.length;

      resultado.totalAtualizado = dataDetalhes.titulos.reduce((total, item) => {
        return total + item.valor_atualizado;
      }, 0);
      setValue(
        "total_atualizado",
        Math.round(resultado.totalAtualizado * 100) / 100
      );
      setValue(
        "finalizacao.recebimento_unico.valor_recebido",
        formatarNumero(resultado.totalAtualizado)
      );

      resultado.saldoTotal = dataDetalhes.titulos.reduce((total, item) => {
        return total + item.saldo;
      }, 0);
      setValue("total", formatarNumero(resultado.saldoTotal));

      resultado.juros = dataDetalhes.juros;
      setValue("juros_atualizado", resultado.juros);

      return resultado;
    }, [dataDetalhes, setValue]);

  const isPix = formRecb === "emitir pix";
  const isBoleto = formRecb === "emitir boleto bancario";
  //

  //Funções manipuladoras
  const handleImprimir = () => {
    const params = new URLSearchParams();

    titulos.forEach((titulo) => {
      params.append("titulos[]", titulo.id.toString());
    });
    params.append("data_recebimento", dataReceb.format("YYYY-MM-DD"));

    imprimirArquivo(
      `financeiro/contas-receber/imprimir-extrato-titulos?${params.toString()}`
    );
  };

  return (
    <Box
      p={3}
      borderRadius={1}
      bgcolor={"#f5f5f5"}
      width={650}
      height={{ md: "unset", lg: 254 }}
      boxShadow={2}
    >
      {isLoading && (
        <LoadingApiQuery
          extraParams={{ height: "100%", alignItems: "center" }}
        />
      )}

      {!isLoading && (
        <>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="flex-start"
          >
            <Typography mb={5} variant="h6">
              Títulos selecionados
            </Typography>
            <Box>
              <Tooltip title="Imprimir extrato">
                <IconButton onClick={() => handleImprimir()}>
                  <PrintIcon />
                </IconButton>
              </Tooltip>
              <IconButton
                onClick={() => setMostrarValor((prevState) => !prevState)}
              >
                {mostrarValor ? <RemoveRedEyeIcon /> : <VisibilityOffIcon />}
              </IconButton>
            </Box>
          </Box>

          <Grid container spacing={1} rowGap={1}>
            <Grid size={{xs: 6, md: 6, lg: 4}}>
              <LabelPrinter
                size="small"
                titulo="Qtd. títulos"
                valor={quantidadeItens.toString()}
              />
            </Grid>

            <Grid size={{xs: 6, md: 6, lg: 4}}>
              <LabelPrinter
                size="small"
                titulo="Saldo total"
                valor={formatarValorMonetario(saldoTotal)}
              />
            </Grid>

            <Grid size={{xs: 6, md: 6, lg: 4}}>
              <LabelPrinter
                size="small"
                editavel={!isPix && !isBoleto}
                titulo="Data do recebimento"
                valor={dataReceb.format("DD/MM/YYYY")}
                editAcao={() => setModalDataRecb(true)}
              />
            </Grid>

            <Grid size={{xs: 12, md: 4}}>
              <LabelPrinter
                titulo="Total atualizado"
                valor={formatarValorMonetario(totalAtualizado)}
              />
            </Grid>

            <Grid size={{xs: 12, md: 4}}>
              <LabelPrinter
                mostrarValor={mostrarValor}
                size="small"
                titulo="Juros"
                valor={formatarValorMonetario(juros)}
              />
            </Grid>

            {podeDetalhar && (
                <Grid size={{xs: 12, md: 4}} alignSelf="flex-end" textAlign="right">
                  <Button
                    variant="outlined"
                    onClick={() =>
                      setDetalhesModal({ openModal: true, titulos: titulos })
                    }
                    sx={{ mt: 1, mr: 1 }}
                  >
                    Ver detalhes
                  </Button>
                </Grid>
              )}
          </Grid>

          {modalDataRecb && (
            <DialogSelecionarData
              open={modalDataRecb}
              handleClose={() => setModalDataRecb(false)}
              blockPastDates={true}
              blockFutureDates={true}
              minDate={moment(dataMaisRecente)}
            />
          )}

          {detalhesModal.openModal && (
            <DialogDetalhesTitulos
              totalAtualizado={totalAtualizado}
              open={detalhesModal.openModal}
              onClose={() =>
                setDetalhesModal({ openModal: false, titulos: [] })
              }
              titulosDetalhes={dataDetalhes.titulos}
            />
          )}
        </>
      )}
    </Box>
  );
}
